import React from 'react';

import {MAX_PRIMER_NAME_LENGTH} from 'constants/PrimerConstants';
import makeDeprecatedJadeStyleTemplate from 'util/makeDeprecatedJadeStyleTemplate';
import {MELTING_TEMP_ABBREVIATION} from 'util/UnicodeSymbols';

import EditPrimerBasesWarning from './EditPrimerBasesWarning';

export interface EditPrimerPairProps {
  assemblyPrimerHelp: string;
  isGoldenGate: boolean;
  showEnzymes: boolean;
  primer1Exists: boolean;
  primer2Exists: boolean;
}

function EditPrimerPair({
  assemblyPrimerHelp,
  isGoldenGate,
  showEnzymes,
  primer1Exists,
  primer2Exists,
}: EditPrimerPairProps): JSX.Element {
  return (
    <form className="buffer-content-container form-horizontal">
      <fieldset>
        <legend>Design</legend>
        <div className="row js-use-existing-primer-group use-existing-primer hidden">
          <div className="col col-md-5 col-md-offset-2 js-use-existing-primer invisible" />
          <div data-index="1" className="col col-md-5 js-use-existing-primer invisible" />
        </div>
        <div className="form-group">
          <label className="col-md-2 control-label">Strand</label>
          <div className="col col-md-3">
            <div className="input-field pair-strand">Forward</div>
          </div>
          <div className="col col-md-3 col-md-offset-2">
            <div className="input-field pair-strand">Reverse</div>
          </div>
        </div>
        <div className="form-group">
          <label className="col-md-2 control-label">Bases</label>
          <div className="col col-md-10">
            <div className="row">
              <div className="col col-md-6">
                {primer1Exists && <EditPrimerBasesWarning />}
                <div className="sequence-primer-bases">
                  <span className="prime five-prime">5'</span>
                  <div className="primer-text-outer mono">
                    <div contentEditable={true} className="primer-text" />
                  </div>
                  <span className="prime three-prime">3'</span>
                </div>
              </div>
              <div className="col col-md-6">
                {primer2Exists && <EditPrimerBasesWarning />}
                <div className="sequence-primer-bases">
                  <span className="prime five-prime">5'</span>
                  <div className="primer-text-outer mono">
                    <div contentEditable={true} data-index="1" className="primer-text" />
                  </div>
                  <span className="prime three-prime">3'</span>
                </div>
              </div>
            </div>
            <span
              // oxlint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{__html: assemblyPrimerHelp}}
            />
          </div>
        </div>
        <div className="form-group too-short-msg-group">
          <div className="col col-md-4 col-md-offset-2 too-short-msg">
            <p className="error-msg">Primer must be at least 6 bp.</p>
          </div>
          <div className="col col-md-4 col-md-offset-1 too-short-msg">
            <p className="error-msg">Primer must be at least 6 bp.</p>
          </div>
        </div>
        <div className="row js-found-existing-primer-group hidden">
          <div className="col col-md-5 col-md-offset-2 js-found-existing-primer invisible" />
          <div data-index="1" className="col col-md-5 js-found-existing-primer invisible" />
        </div>
        <div className="form-group primer-position-group">
          <label className="col-md-2 control-label">3' Location</label>
          <div className="col col-md-3 primer-position">
            <input
              type="number"
              name="primer-start-position"
              placeholder="3' end"
              className="form-control js-primer-position"
            />
          </div>
          <div className="col col-md-3 col-md-offset-2 primer-position">
            <input
              type="number"
              name="primer-start-position"
              placeholder="3' end"
              data-index="1"
              className="form-control js-primer-position"
            />
          </div>
        </div>
        <div className="form-group bad-location-msg-group hidden">
          <div className="col col-md-4 col-md-offset-2 bad-location-msg invisible">
            <p className="error-msg">
              Binding location should be within the sequence. Using bind location of{' '}
              <span className="location-num" /> instead.
            </p>
          </div>
          <div className="col col-md-4 col-md-offset-1 bad-location-msg invisible">
            <p className="error-msg">
              Binding location should be within the sequence. Using bind location of{' '}
              <span className="location-num" /> instead.
            </p>
          </div>
        </div>
        <div className="form-group overhang-group">
          <label className="col-md-2 control-label">Overhang</label>
          <div className="col col-md-3 overhang">
            <input
              type="number"
              name="primer-overhang-size"
              placeholder="0 bp"
              min="0"
              step="1"
              className="form-control js-overhang-size"
            />
          </div>
          <div className="col col-md-3 col-md-offset-2 overhang">
            <input
              type="number"
              name="primer-overhang-size"
              placeholder="0 bp"
              min="0"
              step="1"
              data-index="1"
              className="form-control js-overhang-size"
            />
          </div>
        </div>
        <div className="form-group binding-group hidden">
          <label className="col-md-2 control-label">
            Binding
            {isGoldenGate && (
              <>
                &nbsp;
                <a
                  title="Length of sequence-defined region"
                  className="glyphicon glyphicon-question-sign u-no-text-decoration"
                />
              </>
            )}
          </label>
          <div className="col col-md-3 binding invisible">
            <input
              type="number"
              name="primer-binding"
              placeholder="0 bp"
              min="0"
              step="1"
              className="form-control js-binding"
            />
          </div>
          <div className="col col-md-3 col-md-offset-2 binding invisible">
            <input
              type="number"
              name="primer-binding"
              placeholder="0 bp"
              min="0"
              step="1"
              data-index="1"
              className="form-control js-binding"
            />
          </div>
        </div>
        <div className="form-group homology-group hidden">
          <label className="col-md-2 control-label">Overhang</label>
          <div className="col col-md-3 homology invisible">
            <input
              type="number"
              name="primer-homology"
              placeholder="0 bp"
              min="0"
              step="1"
              className="form-control js-homology"
            />
          </div>
          <div className="col col-md-3 col-md-offset-2 homology invisible">
            <input
              type="number"
              name="primer-homology"
              placeholder="0 bp"
              min="0"
              step="1"
              data-index="1"
              className="form-control js-homology"
            />
          </div>
        </div>
        <div className="form-group pre-recognition-site-group hidden">
          <label className="col-md-2 control-label">
            Pre-recog. Site&nbsp;
            <a
              title="5' flanking bases before recognition site"
              className="glyphicon glyphicon-question-sign u-no-text-decoration"
            />
          </label>
          <div className="col col-md-4 pre-recognition-site invisible">
            <input
              type="text"
              name="primer-pre-recognition-site"
              placeholder="Enter the pre-recognition site bases"
              className="form-control js-pre-recognition-site"
            />
          </div>
          <div className="col col-md-4 col-md-offset-1 pre-recognition-site invisible">
            <input
              type="text"
              name="primer-pre-recognition-site"
              placeholder="Enter the pre-recognition site bases"
              data-index="1"
              className="form-control js-pre-recognition-site"
            />
          </div>
        </div>
        <div className="form-group pre-recognition-site-invalid-bases-msg-group hidden">
          <div className="col col-md-4 col-md-offset-2 pre-recognition-site-invalid-bases-msg invisible">
            <p className="error-msg">
              The pre-recognition site cannot contain the same bases as the recognition site.
            </p>
          </div>
          <div className="col col-md-4 col-md-offset-1 pre-recognition-site-invalid-bases-msg invisible">
            <p className="error-msg">
              The pre-recognition site cannot contain the same bases as the recognition site.
            </p>
          </div>
        </div>
        <div className="form-group post-recognition-site-group hidden">
          <label className="col-md-2 control-label">
            Post-recog. Site&nbsp;
            <a
              title="Base(s) after recognition site"
              className="glyphicon glyphicon-question-sign u-no-text-decoration"
            />
          </label>
          <div className="col col-md-4 post-recognition-site invisible">
            <input
              type="text"
              name="primer-post-recognition-site"
              placeholder="Enter the post-recognition site bases"
              className="form-control js-post-recognition-site"
            />
          </div>
          <div className="col col-md-4 col-md-offset-1 post-recognition-site invisible">
            <input
              type="text"
              name="primer-post-recognition-site"
              placeholder="Enter the post-recognition site bases"
              data-index="1"
              className="form-control js-post-recognition-site"
            />
          </div>
        </div>
        <div className="form-group post-recognition-site-invalid-length-msg-group hidden">
          <div className="col col-md-4 col-md-offset-2 post-recognition-site-invalid-length-msg invisible">
            <p className="error-msg">
              The post-recognition site must be exactly <span className="valid-length" /> bp.
            </p>
          </div>
          <div className="col col-md-4 col-md-offset-1 post-recognition-site-invalid-length-msg invisible">
            <p className="error-msg">
              The post-recognition site must be exactly <span className="valid-length" /> bp.
            </p>
          </div>
        </div>
        <div className="form-group overhang-too-small-msg-group hidden">
          <div className="col col-md-4 col-md-offset-2 overhang-too-small-msg invisible">
            <p className="error-msg">
              Primer bases lying off of the sequence cannot be more than the overhang. Using overhang size of{' '}
              <span className="overhang-num" /> instead.
            </p>
          </div>
          <div className="col col-md-4 col-md-offset-1 overhang-too-small-msg invisible">
            <p className="error-msg">
              Primer bases lying off of the sequence cannot be more than the overhang. Using overhang size of{' '}
              <span className="overhang-num" /> instead.
            </p>
          </div>
        </div>
        <div className="form-group overhang-too-large-msg-group hidden">
          <div className="col col-md-4 col-md-offset-2 overhang-too-large-msg invisible">
            <p className="error-msg">
              Overhang size cannot be longer than primer size. Using overhang size of{' '}
              <span className="overhang-num" /> instead.
            </p>
          </div>
          <div className="col col-md-4 col-md-offset-1 overhang-too-large-msg invisible">
            <p className="error-msg">
              Overhang size cannot be longer than primer size. Using overhang size of{' '}
              <span className="overhang-num" /> instead.
            </p>
          </div>
        </div>

        {showEnzymes && (
          <div className="form-group">
            <label className="col-md-2 control-label">Cut Site</label>
            <div className="col col-md-10">
              <div className="row">
                <div className="col col-md-4 js-edit-primer-enzymes" />
                <div className="col col-md-4">
                  <input type="text" readOnly={true} className="form-control js-reference" />
                </div>
              </div>
              <p className="help-block">Use the dropdown above to look up restriction sites.</p>
            </div>
          </div>
        )}
        <div className="form-group js-invalid-pair hidden">
          <p className="error-msg">
            The specified primer pair does not result in a valid product. Make sure that the forward primer
            binds before the reverse primer if the sequence is linear.
          </p>
        </div>
      </fieldset>
      <fieldset>
        <legend>
          Verify
          <div className="pull-right">
            <div className="secondary-temp">
              <button type="button" className="button button--small button--tertiaryGray js-check-secondary">
                Check Secondary Structure
              </button>{' '}
              at <input type="text" placeholder="37" />
              &deg;C
            </div>
          </div>
        </legend>
        <div className="row">
          <label className="col-md-2 control-label">
            {MELTING_TEMP_ABBREVIATION}{' '}
            <span className="settings glyphicon glyphicon-wrench js-tm-parameters u-pointer" />
          </label>
          <div className="col col-md-2 text-right js-tm input-field" />
          <div className="col col-md-2 col-md-offset-3 text-right js-tm input-field" />
        </div>
        <div className="row">
          <label className="col-md-2 control-label">GC Content</label>
          <div className="col col-md-2 text-right js-gc input-field" />
          <div className="col col-md-2 col-md-offset-3 text-right js-gc input-field" />
        </div>
        <div className="row">
          <label className="col-md-2 control-label">Length</label>
          <div className="col col-md-2 text-right js-length input-field" />
          <div className="col col-md-2 col-md-offset-3 text-right js-length input-field" />
        </div>
        <div className="row js-secondary hidden">
          <label className="col-md-2 control-label">Min &Delta;G Homodimer</label>
          <div className="col col-md-2 text-right js-homodimer-dg input-field">
            <div className="spinner" />
          </div>
          <div className="col col-md-3 js-homodimer secondary-link invisible">
            <a>All Structures</a>
          </div>
          <div data-index="1" className="col col-md-2 text-right js-homodimer-dg input-field">
            <div className="spinner" />
          </div>
          <div data-index="1" className="col col-md-3 js-homodimer secondary-link invisible">
            <a>All Structures</a>
          </div>
        </div>
        <div className="row js-secondary hidden">
          <label className="col-md-2 control-label">Min &Delta;G Monomer</label>
          <div className="col col-md-2 text-right js-monomer-dg input-field">
            <div className="spinner" />
          </div>
          <div className="col col-md-3 js-monomer secondary-link invisible">
            <a>All Structures</a>
          </div>
          <div data-index="1" className="col col-md-2 text-right js-monomer-dg input-field">
            <div className="spinner" />
          </div>
          <div data-index="1" className="col col-md-3 js-monomer secondary-link invisible">
            <a>All Structures</a>
          </div>
        </div>
        <hr />
        <div className="row">
          <label className="col col-md-2 control-label">Product Size</label>
          <div className="col col-md-2 text-right js-product-size input-field">--</div>
        </div>
        <div className="row">
          <label className="col col-md-2 control-label">{MELTING_TEMP_ABBREVIATION} Diff.</label>
          <div className="col col-md-2 text-right js-tm-diff input-field">--</div>
        </div>
        <div className="row js-secondary hidden">
          <label className="col col-md-2 control-label heterodimer-title">Min &Delta;G Heterodimer</label>
          <div className="col col-md-2 text-right js-heterodimer-dg input-field">
            <div className="spinner" />
          </div>
          <div className="col col-md-3 js-heterodimer secondary-link invisible">
            <a>All Structures</a>
          </div>
        </div>
      </fieldset>
      <fieldset>
        <legend>Save</legend>
        <div className="form-group">
          <label className="col-md-2 control-label">Name</label>
          <div className="col col-md-4">
            <input type="text" name="primer-name" placeholder="Enter a short name" className="js-name" />
          </div>
          <div className="col col-md-1 text-right">
            <div className="swatch-big" />
          </div>
          <div className="col col-md-4">
            <input
              type="text"
              name="primer-name"
              placeholder="Enter a short name"
              data-index="1"
              className="js-name"
            />
          </div>
          <div className="col col-md-1 text-right">
            <div data-index="1" className="swatch-big" />
          </div>
        </div>
        <div className="col col-md-5 col-md-offset-2 js-name-too-long invisible">
          <p className="text-warning error-msg">
            The primer name you have selected is too long. Primer names can be at most{' '}
            {MAX_PRIMER_NAME_LENGTH} characters long.
          </p>
        </div>
        <div className="col col-md-5 js-name-too-long invisible">
          <p className="text-warning error-msg">
            The primer name you have selected is too long. Primer names can be at most{' '}
            {MAX_PRIMER_NAME_LENGTH} characters long.
          </p>
        </div>
        <div className="form-group js-found-existing-name-group hidden">
          <div className="col col-md-5 col-md-offset-2 js-found-existing-name invisible">
            <p className="text-warning error-msg">
              A primer with name "<span className="existing-name" />" already exists in the designated folder.
            </p>
          </div>
          <div className="col col-md-5 js-found-existing-name invisible">
            <p className="text-warning error-msg">
              A primer with name "<span className="existing-name" />" already exists in the designated folder.
            </p>
          </div>
        </div>
        <div className="form-group save-library-group">
          <label className="col-md-2 control-label">Save To</label>
          <div className="col col-md-4 save-library">
            <div className="folder-selector-container" />
          </div>
          <div className="col col-md-4 col-md-offset-1 save-library">
            <div className="folder-selector-container" />
          </div>
        </div>
        <div className="form-group">
          <div className="col col-md-3 col-md-offset-8">
            <div className="pull-right">
              <button type="button" className="button button--small button--primary is-disabled save-pair">
                Save Primer Pair
              </button>
            </div>
          </div>
        </div>
      </fieldset>
    </form>
  );
}

export default makeDeprecatedJadeStyleTemplate<EditPrimerPairProps>(EditPrimerPair);
